import { useDispatch, useSelector } from 'react-redux'
import { selectRecentDispatches } from 'redux/appStore'
import React from 'react'
import { addRecentAllstateDispatch, updateRecentAllstateDispatchStatusById } from 'redux/recentDispatch/recentDispatchSlice'
import { useServiceProxy } from '../kong'
import { deepMapSanitize } from '../../utils/deepMap'
import { isNil } from 'lodash-es'
import { allStateData } from '../../types/allstate'
import { ProviderNames } from 'hooks/dispatch/utils'

export function useGetAllstateDispatches(dispatchId?: string | null, provider?: string | null) {
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [data, setData] = React.useState<any | null>(null)

  // Redux functions
  const dispatch = useDispatch()
  const dispatches = useSelector(selectRecentDispatches)
  const addRecentDispatch = (newDispatch: any) => dispatch(addRecentAllstateDispatch(newDispatch))
  const updateRecentDispatch = (data: any) => dispatch(updateRecentAllstateDispatchStatusById(data))

  const serviceProxy = useServiceProxy()
  const shouldFireQuery = React.useRef<boolean>(false)
  const didCancel = React.useRef(false)

  const query = React.useCallback(
    async (shouldFireQuery: boolean) => {
      if (provider && provider !== ProviderNames.ALLSTATE) {
        return
      }
      if (shouldFireQuery) {
        try {
          const { data } = await serviceProxy<allStateData>(
            'get',
            `/serviceproxy/allstate/api/rescue/v2/info?callId=${dispatchId}`,
          )
          const dispatchData = deepMapSanitize(data) as allStateData
          setData(dispatchData)
        } catch (e) {
          console.error(e)
          setError('Unable to lookup Allstate dispatch')
        }
      }
    },
    [dispatchId, serviceProxy],
  )

  React.useEffect(() => {
    if (!dispatchId || !isNil(data)) return

    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        await query(true)
      } catch (err) {
        if (!didCancel.current) {
          setError(err as any)
        }
      } finally {
        if (!didCancel.current) {
          setLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      didCancel.current = true
    }
  }, [dispatchId])

  React.useEffect(() => {
    let recentDispatch = dispatches.AllstateDispatches.find((dispatch) => dispatch?.serviceRequest.callId === dispatchId)

    if (recentDispatch !== undefined) {
      if (data?.serviceRequest?.status && recentDispatch?.serviceRequest?.status !== data?.serviceRequest?.status) {
        // the data is refetched for Cancel dispatch scenario
        setData(data)
        updateRecentDispatch(data)
      } else {
        setData(recentDispatch)
      }
      setLoading(false)
      shouldFireQuery.current = false
    } else {
      setData(null)
      shouldFireQuery.current = true
    }
    const callId = recentDispatch?.serviceRequest?.callId ?? data?.dispatchId ?? data?.serviceRequest?.callId
    if (data) {
      // Safely check if a dispatch with the same callId already exists
      const exists = dispatches?.AllstateDispatches?.some((d) => d?.serviceRequest?.callId === callId)
      if (!exists) {
        addRecentDispatch(data)
      }
    }
  }, [shouldFireQuery, dispatchId, data])

  return { data, loading, error, refetch: query }
}
